import { useState } from "react";
import { toast } from "sonner";
import { Check, Send } from "lucide-react";
import { submitContact } from "@/lib/api";
import { track } from "@/lib/analytics";
import { TID } from "@/constants/testIds";

/**
 * Compact "Reach us" panel embedded inside the site footer on every
 * non-admin / non-EOI page. Hits POST /api/leads/contact with
 * source="footer_contact". A minimal 3-field form (name + mobile + email)
 * plus a short message textarea — the footer's job is discovery, not the
 * full qualification flow.
 */
export default function FooterContactPanel() {
  const [values, setValues] = useState({
    name: "",
    mobile: "",
    email: "",
    message: "",
    consent: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const onChange = (k) => (e) => {
    const v = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setValues((s) => ({ ...s, [k]: v }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!values.consent) {
      toast.error("Please tick the consent box before submitting.");
      return;
    }
    setSubmitting(true);
    try {
      // Backend requires `city` and `profile`, so we synthesise minimal
      // values from the message textbox so this compact footer form still
      // matches the ContactLeadCreate schema.
      await submitContact({
        name: values.name,
        mobile: values.mobile,
        email: values.email,
        city: "Not shared (footer form)",
        profile: values.message || "Footer enquiry",
        consent: true,
        source: "footer_contact",
      });
      setDone(true);
      track("form_submit", { form: "footer_contact", source: "footer_contact", email: values.email, mobile: values.mobile, name: values.name });
      toast.success("Received. We'll be in touch, unhurriedly.");
    } catch (err) {
      track("form_error", { form: "footer_contact", source: "footer_contact" });
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Something went wrong.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="rounded-2xl border border-lime/30 bg-forest/40 p-6 flex items-center gap-4">
        <div className="w-10 h-10 rounded-full bg-lime grid place-items-center flex-none">
          <Check className="text-void" size={18} />
        </div>
        <div>
          <div className="font-display text-lg text-cream">Received.</div>
          <div className="text-xs text-cream/70">Someone from our team will reach out shortly.</div>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-3.5">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3.5">
        <input
          data-testid={TID.footerContactName}
          required
          placeholder="Name *"
          value={values.name}
          onChange={onChange("name")}
          className={fieldCls}
        />
        <input
          data-testid={TID.footerContactMobile}
          required
          placeholder="Mobile *"
          inputMode="tel"
          value={values.mobile}
          onChange={onChange("mobile")}
          className={fieldCls}
        />
        <input
          data-testid={TID.footerContactEmail}
          required
          placeholder="Email *"
          type="email"
          value={values.email}
          onChange={onChange("email")}
          className={fieldCls}
        />
      </div>
      <textarea
        data-testid={TID.footerContactMessage}
        rows={3}
        placeholder="How can we help?"
        value={values.message}
        onChange={onChange("message")}
        className={`${fieldCls} resize-none`}
      />
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pt-1">
        <label className="flex items-start gap-2 max-w-xl">
          <input
            data-testid={TID.footerContactConsent}
            type="checkbox"
            required
            checked={values.consent}
            onChange={onChange("consent")}
            className="mt-1 w-4 h-4 accent-[color:var(--phc-lime)]"
          />
          <span className="text-[11px] text-cream/60 leading-relaxed">
            I consent to being contacted via WhatsApp, Call, and SMS regarding
            Pali Hill City. (TRAI)
          </span>
        </label>
        <button
          data-testid={TID.footerContactSubmit}
          disabled={submitting}
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm bg-lime text-void hover:bg-cream transition-colors font-medium disabled:opacity-60 whitespace-nowrap"
        >
          <Send size={14} /> {submitting ? "Sending…" : "Send message"}
        </button>
      </div>
    </form>
  );
}

const fieldCls =
  "w-full bg-white/5 border border-cream/15 rounded-lg py-2.5 px-3.5 text-cream placeholder:text-cream/40 focus:outline-none focus:border-lime transition-colors text-[14px] font-body";
